




























































































































































































































/* eslint-disable max-len */
import {
  defineComponent, reactive, toRefs, ref, watch,
} from '@vue/composition-api'
import Pod from '@/components/layouts/Pod.vue'
import Editor from '@/components/appBase/Editor.vue'
import ApiError from '@/components/appBase/errors/ApiError.vue'
import DelayedDisplay from '@/components/appBase/DelayedDisplay.vue'
import { replaceText } from '@/lib/utils/replaceText'
import { vxm } from '@/state/index'
import useApiErrorViewHandler from '@/components/appBase/errors/useApiErrorViewHandler'
import useClientflowApi from '@/api/clientFlow/useClientflowApi'

const emailSubject = 'Velkommen til Psykomotoriske Terapeuter i København'
const mailTemplate = `Kære %name%
<p>Hermed lidt kort information om forløb hos Psykomotoriske Terapeuter København:</p>
<p>Alle klienter tilbydes at deltage i et løbende <span style="font-weight:bold;">forskningsprojekt</span> som sker i samarbejde med Københavns Professionshøjskole - du besvarer nogle korte spørgsmål løbende, og hjælper os derved til hele tiden at udvikle vores praksis så du kan modtage den bedst mulige behandling. </p>
<p>Læs mere her:</p>
<p><span style="font-weight:bold;">[ACCESS_BUTTON]</span></p>
<p><span style="font-weight:bold;">Sådan finder du os</span>: Vi bor på <a href="https://www.google.com/maps/place/Vesterbrogade+20,+1620+K%C3%B8benhavn/@55.6731628,12.5566505,17z/data=!3m1!4b1!4m5!3m4!1s0x4652530b5dcc8043:0x30c2a01fb0ce93a1!8m2!3d55.6731628!4d12.5588445" target="_blank">Vesterbrogade 20</a> i København, vores klinik hedder Tværfagligt Hus det er det navn du skal ringe på på dørtelefonen, så kan du gå lige ind og op på klinikken på 2.sal.</p>
<p><span style="font-weight:bold;">Afbudsregler</span>: Du og andre kan få behov for at få tilbudt en akut afbudstid. Det forudsætter dog at vi modtager afbud tids nok til at kunne ringe videre til de klienter der står på venteliste. Derfor skal afbud ske senest dagen før inden kl.12.00. Meget gerne tidligere, jo før jo bedre :-) Afbud gives på mail direkte til din terapeut eller på telefon 71992148. Ved udeblivelse eller for sen aflysning vil du blive faktureret for den bestilte tid.</p>`

const getDanishPhoneNumberRegExp = () => RegExp(/\+?\b(?:45\s|45\s|45|)(?:\d{2}\s?){3}\d{2}\b/g)
const isPhoneNumber = (val: string, regExp: RegExp) => regExp.test(val.trim())
const validationRules = {
  email: [
    (v: string) => !!v || 'E-mail er obligatorisk',
    (v: string) => /.+@.+\..+/.test(v) || 'E-mail adressen skal være gyldig',
  ],
  name: [
    (v: string) => !!v || 'Navn er obligatorisk',
    (v: string) => (v && v.length >= 2) || 'Navnet skal bestå af mindst to bogstaver',
  ],
  phone: [(v: string) => v.trim() === ''
    || isPhoneNumber(v, getDanishPhoneNumberRegExp())
    || 'Telefonnr skal være et gyldigt dansk tlfnr',
  ],
}

interface NewClientFields {
  name: string;
  email: string;
  phone: string;
  isDummy: false;
}

interface EmailFields {
  emailText: string;
  emailSubject: string;
}

type State = NewClientFields & EmailFields & {
  snackbar: boolean;
  e1: number;
  currentStep: number;
  valid: boolean;
  editorIsFocused: boolean;
}

const initialState: State = {
  snackbar: false,
  emailText: '',
  emailSubject,
  e1: 0,
  currentStep: 1,
  name: '',
  email: '',
  phone: '',
  isDummy: false,
  valid: false,
  editorIsFocused: false,
}

export default defineComponent({
  components: {
    Editor, ApiError, DelayedDisplay, Pod,
  },

  setup(props, { emit }) {
    // Set up state
    let state = reactive({ ...initialState })

    // refs in the template (remember to also `return` them)
    // these are components that provide functionality (functions)
    const mailEditorRef = ref(null)
    const createClientFormRef = ref(null)

    // Compose!
    // ...delegate api handling to useClientflowApi():
    const {
      ClientFlowApi,
      awaitingResponse,
      hasResponseError,
      handler,
      errorDisplay,
    } = useClientflowApi()

    // ...delegate logic around showing api error to useApiErrorViewHandler():
    const {
      errorVisibilityHasChangedTo,
      toggleShowApiError,
      showResponseError,
      showingResponseError,
    } = useApiErrorViewHandler(hasResponseError)

    function formIsValid(): boolean {
      const formRef = (createClientFormRef.value as unknown as & { validate: () => boolean })
      return formRef.validate()
    }

    function stepToMailEditor() {
      if (!formIsValid()) {
        return
      }
      state.emailText = replaceText(mailTemplate, { '%name%': state.name })
      state.currentStep = 2
    }

    watch(
      () => state.currentStep,
      (currentStep) => {
        if (currentStep === 2) {
          stepToMailEditor()
        }
      },
    )

    function cancel(): void {
      state = reactive({ ...initialState })
      emit('cancelled')
    }

    function finish(): void {
      emit('finished')
    }

    async function createClientAndSendInvite() {
      const emailHtml = (mailEditorRef.value as unknown as & { getContentAsHtml: () => string }).getContentAsHtml()
      const cmd = await ClientFlowApi.addNewClient({
        name: state.name.trim(),
        email: state.email.trim(),
        // phone: this.phone.trim(),
        isDummy: state.isDummy,
        inviteMailSubject: state.emailSubject,
        inviteMailBody: emailHtml,
        therapistId: vxm.authUser.id ? vxm.authUser.id : '',
      })
      const response = await handler.handle(cmd)
      if (response.isOk()) {
        emit('client-created', {
          name: state.name.trim(),
          id: response.value.clientId,
        })
        finish()
      }
    }

    // Make stuff available for the template
    return {
      // from self
      ...toRefs(state),
      createClientFormRef,
      mailEditorRef,
      validationRules,
      stepToMailEditor,
      createClientAndSendInvite,
      cancel,

      // from use ClienFlowApi
      hasResponseError,
      awaitingResponse,
      errorDisplay,

      // from useApiErrorViewHandler
      showingResponseError,
      showResponseError,
      toggleShowApiError,
      errorVisibilityHasChangedTo,
    }
  },
})

